import api from '../../../services/api';
import logger from '../../../utils/logging/logger';

// The one-time donation confirm branch of handleSubmit, extracted VERBATIM
// (the `else` of `if (isSubscriptionPayment)`, inside `if (clientSecret &&
// stripe && elements)`). Not wrapped in its own try/catch — exceptions
// propagate to the caller's try/catch exactly as they did inline.
export const submitOneTimeDonation = async ({
  stripe,
  elements,
  clientSecret,
  preservedOneTimeAmount,
  oneTimeAmount,
  email,
  firstName,
  lastName,
  user,
  navigate,
  setError,
  setLoading,
}) => {
  // One-time donation flow
  // Validate that we still have the amount (use preserved amount if available)
  const amountToUse = preservedOneTimeAmount || parseFloat(oneTimeAmount);
  if (!amountToUse || amountToUse <= 0) {
    setError('Please enter a valid donation amount.');
    setLoading(false);
    return;
  }

  // Confirm the payment
  // Include receipt_email in confirmParams to ensure email is captured
  const confirmParams = {
    return_url: `${window.location.origin}/portal/donation/success`,
  };

  // Add receipt_email if email is available (helps Stripe capture it)
  if (email && email.trim()) {
    confirmParams.receipt_email = email.trim();
  }

  const { error: confirmError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmPayment({
    elements,
    clientSecret: clientSecret,
    confirmParams: confirmParams,
    redirect: 'if_required',
  });

  if (confirmError) {
    setError(confirmError.message || 'Payment failed. Please try again.');
    setLoading(false);
    return;
  }

  // Get payment intent ID from the confirmation response (most reliable)
  // If not available, retrieve it separately
  let paymentIntent = confirmedPaymentIntent;
  let paymentIntentId = confirmedPaymentIntent?.id;

  if (!paymentIntentId) {
    // Fallback: Retrieve payment intent with expanded charges to get receipt URL
    logger.debug('Payment intent not in confirmPayment response, retrieving separately...');
    try {
      const result = await stripe.retrievePaymentIntent(clientSecret, {
        expand: ['charges']
      });
      paymentIntent = result.paymentIntent;
      paymentIntentId = paymentIntent?.id;
    } catch (retrieveError) {
      logger.error('Error retrieving payment intent', { error: retrieveError });
      // Try to extract from client secret as last resort
      const fallbackId = clientSecret.split('_secret_')[0];
      if (fallbackId && fallbackId.startsWith('pi_')) {
        paymentIntentId = fallbackId;
        logger.debug('Using fallback payment intent ID from client secret', { fallbackId });
      } else {
        setError('Could not retrieve payment intent ID. Please contact support.');
        setLoading(false);
        return;
      }
    }
  }

  if (!paymentIntentId) {
    setError('Could not retrieve payment intent ID. Please contact support.');
    setLoading(false);
    return;
  }

  logger.debug('Sending payment intent ID to backend', {
    status: paymentIntent?.status,
    amount: paymentIntent?.amount
  });

  // Confirm donation on backend (works for both authenticated and guest users)
  // Add a small delay to ensure Stripe has processed the payment intent
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    // Use publicRequest for guest donations, request for authenticated users
    if (user) {
      await api.request('/payments/confirm-donation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: paymentIntentId
        })
      });
    } else {
      await api.publicRequest('/payments/confirm-donation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: paymentIntentId
        })
      });
    }
    logger.debug('Backend confirmation successful');
  } catch (confirmErr) {
    // Continue anyway as payment was successful
    logger.warn('Backend confirmation failed, but payment was successful', { error: confirmErr });
    logger.warn('Error details', {
      message: confirmErr.message,
      response: confirmErr.response
    });
  }

  // Get receipt URL from payment intent
  const receiptUrl = paymentIntent?.charges?.data?.[0]?.receipt_url || null;

  // Navigate to confirmation page with donation data (use correct path without /portal prefix due to basename)
  navigate('/donation/success', {
    state: {
      donationData: {
        donationType: 'one-time',
        oneTimeAmount: amountToUse,
        totalAmount: amountToUse,
        user: {
          firstName: firstName,
          lastName: lastName,
          email: email,
          first_name: firstName,
          last_name: lastName
        },
        receiptUrl: receiptUrl,
        paymentIntentId: paymentIntentId
      }
    }
  });
};
